import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import axios, { API, authHeaders, formatError } from "../../lib/api";

const STATUSES = [["new", "New"], ["contacted", "Contacted"], ["rate_agreed", "Rate agreed"], ["picked_up", "Picked up"], ["closed", "Closed"]];
const MATERIALS = { rice_husk: "Rice husk", paddy_straw: "Paddy straw", wheat_bhusa: "Wheat bhusa", mustard_stalk: "Mustard stalk", other: "Other" };

export const SupplyTable = ({ onAuthFail }) => {
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/supply`, { headers: authHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error(formatError(err));
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/supply/${id}`, { status }, { headers: authHeaders() });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Supply offer updated");
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (!rows.length) return <p data-testid="admin-supply-empty" className="text-[var(--sage)]">No supply offers yet.</p>;

  return (
    <div data-testid="admin-supply-list" className="grid gap-3">
      {rows.map((r) => (
        <div key={r.id} data-testid={`admin-supply-${r.id}`} className="rounded-[20px] border hairline bg-[var(--forest-2)] p-5 grid gap-4 md:grid-cols-[1.2fr_1fr_1.4fr_170px] items-start">
          <div>
            <p className="font-mono text-xs text-[var(--husk)]">{r.ref}</p>
            <p className="font-semibold text-lg">{r.name}</p>
            <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 text-sm text-[var(--mint)]"><Phone className="h-3.5 w-3.5" />{r.phone}</a>
          </div>
          <div>
            <p className="font-display text-2xl font-bold">{r.quantity_t} t</p>
            <p className="text-sm text-[var(--sage)]">{MATERIALS[r.material] || r.material} · from {r.available_month}</p>
            <p className="font-mono text-[11px] text-[var(--sage)] mt-1">{new Date(r.created_at).toLocaleString("en-IN")}</p>
            <p data-testid={`admin-supply-alerts-${r.id}`} className="font-mono text-[11px] text-[var(--sage)] mt-1">Alerts · email {r.alerts?.email ?? "—"} · WhatsApp {r.alerts?.whatsapp ?? "—"}</p>
          </div>
          <div className="text-sm text-[var(--sage)]">
            <p className="text-[var(--cream)]">{r.village}</p>
            {r.email && <a href={`mailto:${r.email}`} className="mt-1 block text-[var(--mint)]">{r.email}</a>}
            {r.notes && <p className="mt-1 italic">{r.notes}</p>}
          </div>
          <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
            <SelectTrigger data-testid={`admin-supply-status-select-${r.id}`} className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
};
